import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, addDays } from 'date-fns';
import Icon from 'components/AppIcon';

const PLAN_DATA = {
  starter: {
    name: 'Starter',
    badge: '30-Day Free Trial',
    trialDays: 30,
    price: 'Free',
    nextBilling: 'No charge — upgrade or cancel before trial ends',
    color: 'text-slate-400',
    bgColor: 'bg-slate-800/40',
    borderColor: 'border-slate-700/60',
    features: [
      { icon: 'Users', text: 'Up to 5 active clients' },
      { icon: 'Dumbbell', text: 'Workout Builder' },
      { icon: 'CalendarPlus', text: 'Client Plan Builder (7-day weekly plans)' },
      { icon: 'Apple', text: 'Nutrition Plan Builder' },
      { icon: 'MessageCircle', text: 'Client Messaging' },
      { icon: 'TrendingUp', text: 'Progress Tracking' },
    ],
  },
  pro: {
    name: 'Pro',
    badge: 'Pro Plan',
    trialDays: 0,
    price: '$49/mo',
    nextBilling: 'Billed monthly — cancel anytime',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/30',
    features: [
      { icon: 'Users', text: 'Up to 20 active clients' },
      { icon: 'Dumbbell', text: 'Workout Builder' },
      { icon: 'CalendarPlus', text: 'Client Plan Builder (7-day weekly plans)' },
      { icon: 'Apple', text: 'Nutrition Plan Builder' },
      { icon: 'MessageCircle', text: 'Client Messaging' },
      { icon: 'TrendingUp', text: 'Progress Tracking' },
      { icon: 'Palette', text: 'Custom Branding' },
      { icon: 'BarChart3', text: 'Predictive Analytics' },
      { icon: 'Watch', text: 'Wearable Device Sync' },
      { icon: 'Headphones', text: 'Priority Support' },
    ],
  },
};

const NEXT_STEPS = [
  {
    step: '01',
    icon: 'UserPlus',
    title: 'Add your first client',
    description: 'Invite clients to the platform and complete their intake assessment.',
    action: '/trainer-dashboard',
    actionLabel: 'Go to Dashboard',
  },
  {
    step: '02',
    icon: 'CalendarPlus',
    title: 'Build a weekly workout plan',
    description: 'Use the 7-day plan builder to assign exercises and rest days for each client.',
    action: '/trainer-workout-builder',
    actionLabel: 'Open Plan Builder',
  },
  {
    step: '03',
    icon: 'Apple',
    title: 'Set up a nutrition plan',
    description: 'Create personalised meal plans with macros and daily calorie targets.',
    action: '/trainer-nutrition-plan-builder',
    actionLabel: 'Open Nutrition Builder',
  },
];

const RegistrationConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams?.get('plan') || 'starter';
  const trainerName = searchParams?.get('name') || '';

  const plan = PLAN_DATA?.[planId] || PLAN_DATA?.starter;

  const dates = useMemo(() => {
    const today = new Date();
    const trialEnd = plan?.trialDays > 0 ? addDays(today, plan?.trialDays) : null;
    return {
      start: format(today, 'MMMM d, yyyy'),
      end: trialEnd ? format(trialEnd, 'MMMM d, yyyy') : null,
    };
  }, [plan]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/40 bg-card/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-7 h-7 wellness-gradient-primary rounded-lg">
              <Icon name="Heart" size={14} color="white" />
            </div>
            <span className="font-bold text-foreground">Form Forge</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="CheckCircle" size={15} className="text-primary" />
            <span>Account created successfully</span>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero confirmation */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl wellness-gradient-primary mb-5 shadow-lg">
            <Icon name="CheckCircle" size={30} color="white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3 tracking-tight">
            {trainerName ? `Welcome, ${trainerName}!` : "You're all set!"}
          </h1>
          <p className="text-muted-foreground text-base max-w-md mx-auto">
            {plan?.trialDays > 0
              ? `Your ${plan?.trialDays}-day free trial is now active. Explore every feature — no credit card needed.`
              : 'Your Pro account is active. Start building plans for your clients right away.'}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Trial / Plan dates card */}
          <motion.div
            className={`rounded-2xl border ${plan?.borderColor} bg-card p-6`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <div className="flex items-center space-x-2 mb-5">
              <div className={`w-8 h-8 rounded-lg ${plan?.bgColor} flex items-center justify-center`}>
                <Icon name="Calendar" size={16} className={plan?.color} />
              </div>
              <h2 className="font-semibold text-foreground">
                {plan?.trialDays > 0 ? 'Trial Period' : 'Subscription'}
              </h2>
              <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full ${plan?.bgColor} ${plan?.color}`}>
                {plan?.badge}
              </span>
            </div>

            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {plan?.trialDays > 0 ? 'Trial starts' : 'Active from'}
                  </p>
                  <p className="text-sm font-semibold text-foreground">{dates?.start}</p>
                </div>
                {dates?.end && (
                  <>
                    <div className="flex items-center self-center">
                      <div className="w-8 h-px bg-border mx-2" />
                      <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                      <div className="w-8 h-px bg-border mx-2" />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Trial ends</p>
                      <p className="text-sm font-semibold text-foreground">{dates?.end}</p>
                    </div>
                  </>
                )}
              </div>

              {plan?.trialDays > 0 && (
                <div className="bg-muted/40 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Trial progress</span>
                    <span className="text-xs font-medium text-foreground">Day 1 of {plan?.trialDays}</span>
                  </div>
                  <div className="w-full bg-border/60 rounded-full h-1.5">
                    <div className="wellness-gradient-primary h-1.5 rounded-full" style={{ width: '2%' }} />
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-2 pt-1">
                <Icon name="Info" size={13} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">{plan?.nextBilling}</p>
              </div>
            </div>
          </motion.div>

          {/* Included features card */}
          <motion.div
            className="rounded-2xl border border-border/50 bg-card p-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.15 }}
          >
            <div className="flex items-center space-x-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Sparkles" size={16} className="text-primary" />
              </div>
              <h2 className="font-semibold text-foreground">What's included</h2>
            </div>

            <ul className="space-y-2.5">
              {plan?.features?.map((feature, i) => (
                <motion.li
                  key={feature?.text}
                  className="flex items-center space-x-3"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 + i * 0.05 }}
                >
                  <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Icon name="Check" size={11} className="text-primary" />
                  </div>
                  <span className="text-sm text-foreground/80">{feature?.text}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Next steps */}
        <motion.div
          className="rounded-2xl border border-border/50 bg-card p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.25 }}
        >
          <div className="flex items-center space-x-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Rocket" size={16} className="text-primary" />
            </div>
            <h2 className="font-semibold text-foreground">Get started in 3 steps</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {NEXT_STEPS?.map((step, i) => (
              <motion.div
                key={step?.step}
                className="relative bg-muted/30 rounded-xl p-5 border border-border/40 hover:border-primary/30 transition-colors group"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.3 + i * 0.08 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon name={step?.icon} size={17} className="text-primary" />
                  </div>
                  <span className="text-2xl font-black text-border/60 group-hover:text-primary/20 transition-colors leading-none">
                    {step?.step}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1.5">{step?.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-4">{step?.description}</p>
                <button
                  onClick={() => navigate(step?.action)}
                  className="text-xs font-medium text-primary hover:underline flex items-center space-x-1 group-hover:space-x-1.5 transition-all"
                >
                  <span>{step?.actionLabel}</span>
                  <Icon name="ArrowRight" size={12} />
                </button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <button
            onClick={() => navigate('/trainer-dashboard')}
            className="inline-flex items-center space-x-2 wellness-gradient-primary text-white px-8 py-3.5 rounded-xl font-semibold text-sm shadow-lg hover:opacity-90 transition-opacity"
          >
            <span>Go to Trainer Dashboard</span>
            <Icon name="ArrowRight" size={16} />
          </button>
          <p className="text-xs text-muted-foreground mt-4">
            Need help?{' '}
            <button
              onClick={() => navigate('/communication-hub')}
              className="text-primary hover:underline font-medium"
            >
              Contact support
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegistrationConfirmationPage;
